import { Box, Button, Stack, Typography } from "@mui/material";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { CampaignListTableWrapper } from "Components/Admin/Campaigns/CampaignListTable/CampaignListTableWrapper";
import { DeletedCampaignListTableWrapper } from "Components/Admin/Campaigns/DeletedCampaignListTable/DeletedCampaignListTableWrapper";
import { useAdminCampaignsQuery } from "Api/Queries/Admin/useAdminCampaignsQuery";
import { Link } from "Navigation/Link";
import { appUrl } from "Utils/UrlUtils";
import AddIcon from "@mui/icons-material/Add";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { type ReactNode, useMemo } from "react";
import { Resources, useResource } from "Translations/Resources";
import {
  CampaignDisplayStatus,
  CampaignLifecycleStatus,
} from "Infrastructure/Api/Api";
import { useServerTable } from "Hooks/useServerTable";
import type { AdminCampaignDto } from "Infrastructure/Api/Api";
import { asEnumValue, asStartOfDayISO, asString } from "Utils/FilterUtils";
import type { AdminCampaignsParams } from "Api/Queries/Admin/useAdminCampaignsQuery";

const RESOURCES = Resources.Admin.Campaigns;

const DEFAULT_SORTING = [{ id: "dateCreated", desc: true }] as const;
const ARCHIVED_DEFAULT_SORTING = [{ id: "dateUpdated", desc: true }] as const;

// Keep in sync with AdminCampaignsFilterRequest.AllowedSortColumns on BE.
const SORT_COLUMN_MAP: Record<string, string> = {
  name: "Name",
  code: "Code",
  status: "Status",
  startDate: "StartDate",
  endDate: "EndDate",
  dateCreated: "DateCreated",
  dateUpdated: "DateUpdated",
};

const buildCampaignParams = (
  status: CampaignLifecycleStatus,
  queryParams: ReturnType<
    typeof useServerTable<AdminCampaignDto>
  >["queryParams"],
): AdminCampaignsParams => ({
  status,
  offset: queryParams.offset,
  limit: queryParams.limit,
  sortBy: queryParams.sortBy
    ? (SORT_COLUMN_MAP[queryParams.sortBy] ?? queryParams.sortBy)
    : undefined,
  sortDirection: queryParams.sortDirection,
  name: asString(queryParams.filters.name),
  code: asString(queryParams.filters.code),
  receiverCompanyName: asString(queryParams.filters.receiverUserID),
  displayStatus: asEnumValue(CampaignDisplayStatus, queryParams.filters.status),
  startDateFrom: asStartOfDayISO(queryParams.filters.startDate),
  endDateFrom: asStartOfDayISO(queryParams.filters.endDate),
  dateCreatedFrom: asStartOfDayISO(queryParams.filters.dateCreated),
  dateUpdatedFrom: asStartOfDayISO(queryParams.filters.dateUpdated),
});

export const CampaignsPage = () => {
  const { t } = useResource();

  const draftTable = useServerTable<AdminCampaignDto>({
    defaultSorting: [...DEFAULT_SORTING],
  });
  const publishedTable = useServerTable<AdminCampaignDto>({
    defaultSorting: [...DEFAULT_SORTING],
  });
  const archivedTable = useServerTable<AdminCampaignDto>({
    defaultSorting: [...ARCHIVED_DEFAULT_SORTING],
  });

  const draftParams = useMemo(
    () =>
      buildCampaignParams(
        CampaignLifecycleStatus.Draft,
        draftTable.queryParams,
      ),
    [draftTable.queryParams],
  );

  const publishedParams = useMemo(
    () =>
      buildCampaignParams(
        CampaignLifecycleStatus.Published,
        publishedTable.queryParams,
      ),
    [publishedTable.queryParams],
  );

  const archivedParams = useMemo(
    () =>
      buildCampaignParams(
        CampaignLifecycleStatus.Archived,
        archivedTable.queryParams,
      ),
    [archivedTable.queryParams],
  );

  const draftQuery = useAdminCampaignsQuery(draftParams);
  const publishedQuery = useAdminCampaignsQuery(publishedParams);
  const archivedQuery = useAdminCampaignsQuery(archivedParams);

  const allError =
    !draftQuery.isLoading &&
    !publishedQuery.isLoading &&
    !archivedQuery.isLoading &&
    draftQuery.isError &&
    publishedQuery.isError &&
    archivedQuery.isError;

  if (allError) {
    return (
      <Layout>
        <ErrorText>{t(RESOURCES.Messages.LoadCampaignsError)}</ErrorText>
      </Layout>
    );
  }

  const showDraft =
    draftQuery.isLoading || (draftQuery.data?.totalCount ?? 0) > 0;

  const showArchived =
    archivedQuery.isLoading || (archivedQuery.data?.totalCount ?? 0) > 0;

  return (
    <Layout>
      <Stack gap={4}>
        <Box>
          <Button
            component={Link}
            to={appUrl("admin-campaigns-create")}
            variant="contained"
            startIcon={<AddIcon />}
          >
            {t(RESOURCES.CreateButton)}
          </Button>
        </Box>

        {showDraft && (
          <Stack gap={2}>
            <Typography variant="h6" fontWeight={600}>
              {t(RESOURCES.DraftCampaigns)}
            </Typography>
            <Box display="grid" alignItems="start">
              <CampaignListTableWrapper
                data={draftQuery.data?.items ?? []}
                isLoading={draftQuery.isLoading}
                isRefetching={draftQuery.isRefetching}
                rowCount={draftQuery.data?.totalCount ?? 0}
                tableProps={draftTable.tableProps}
              />
            </Box>
          </Stack>
        )}

        <Stack gap={2}>
          <Typography variant="h6" fontWeight={600}>
            {t(RESOURCES.PublishedCampaigns)}
          </Typography>
          <Box display="grid" alignItems="start">
            <CampaignListTableWrapper
              data={publishedQuery.data?.items ?? []}
              isLoading={publishedQuery.isLoading}
              isRefetching={publishedQuery.isRefetching}
              rowCount={publishedQuery.data?.totalCount ?? 0}
              tableProps={publishedTable.tableProps}
            />
          </Box>
        </Stack>

        {showArchived && (
          <Stack gap={2}>
            <Typography variant="h6" fontWeight={600}>
              {t(RESOURCES.ArchivedCampaigns)}
            </Typography>
            <Box display="grid" alignItems="start">
              <DeletedCampaignListTableWrapper
                data={archivedQuery.data?.items ?? []}
                isLoading={archivedQuery.isLoading}
                isRefetching={archivedQuery.isRefetching}
                rowCount={archivedQuery.data?.totalCount ?? 0}
                tableProps={archivedTable.tableProps}
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout title={t(RESOURCES.PageTitle)}>
      <StyledPageWrapper maxWidth="unset !important">
        {children}
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default CampaignsPage;
